import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router";

import {
  type ContactNextActionFormSession,
  useContactNextActionFormSession,
} from "./useContactNextActionFormSession";

import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import { useGetAdminV2MeQuery } from "@/store/api/baseApi";

interface Custody {
  staffId: string;
  firebaseUid: string;
  personId: string;
  organisationId: string | null;
  setOrganisationId: (id: string | null) => void;
  session: ContactNextActionFormSession;
  saveRevision: number;
}
interface ContactActor {
  staffId: string;
  firebaseUid: string;
}

const ContactNextActionCustodyContext = createContext<Custody | null>(null);
const ContactActorRegistrationContext = createContext<
  ((actor: ContactActor) => void) | null
>(null);

/** Keeps an in-memory request with its actor and contact while a read refreshes. */
export function ContactNextActionCustody({
  staffId,
  firebaseUid,
  personId,
  organisationId,
  children,
}: {
  staffId: string;
  firebaseUid: string;
  personId: string;
  organisationId: string | null;
  children: ReactNode;
}) {
  const [saveRevision, setSaveRevision] = useState(0);
  const [currentOrganisationId, setOrganisationId] = useState(organisationId);
  const session = useContactNextActionFormSession({
    personId,
    organisationId: currentOrganisationId,
    onSaved: () => setSaveRevision((revision) => revision + 1),
  });
  return (
    <ContactNextActionCustodyContext.Provider
      value={{
        staffId,
        firebaseUid,
        personId,
        organisationId: currentOrganisationId,
        setOrganisationId,
        session,
        saveRevision,
      }}
    >
      {children}
    </ContactNextActionCustodyContext.Provider>
  );
}

export const useContactNextActionCustody = () =>
  useContext(ContactNextActionCustodyContext);
export const useContactActorRegistration = () =>
  useContext(ContactActorRegistrationContext);

/** Keep the last actor's request owner through a transient identity read. */
export function useRetainedContactActor() {
  const meQuery = useGetAdminV2MeQuery();
  const { user, loading } = useAuthState();
  const staff = meQuery.currentData?.staffUser;
  const staffId = user && staff?.firebaseUid === user.uid ? staff.id : null;
  const firebaseUid = staffId && user ? user.uid : null;
  const currentActor = staffId && firebaseUid ? { staffId, firebaseUid } : null;
  const [retainedActor, setRetainedActor] = useState(currentActor);
  useEffect(() => {
    if (!staffId || !firebaseUid) return;
    setRetainedActor((previous) =>
      previous?.staffId === staffId && previous.firebaseUid === firebaseUid
        ? previous
        : { staffId, firebaseUid },
    );
  }, [staffId, firebaseUid]);
  return (
    currentActor ??
    (retainedActor &&
    ((loading && !user) || user?.uid === retainedActor.firebaseUid)
      ? retainedActor
      : null)
  );
}

/** Owns only the request, not the protected route or its permission grant. */
export function ContactNextActionRouteCustody({
  children,
}: {
  children: ReactNode;
}) {
  const { contactId } = useParams<{ contactId: string }>();
  const { user, loading } = useAuthState();
  const [registeredActor, setRegisteredActor] = useState<ContactActor | null>(
    null,
  );
  const registerActor = useCallback((actor: ContactActor) => {
    setRegisteredActor((previous) =>
      previous?.staffId === actor.staffId &&
      previous.firebaseUid === actor.firebaseUid
        ? previous
        : actor,
    );
  }, []);
  const actor =
    registeredActor &&
    ((loading && !user) || user?.uid === registeredActor.firebaseUid)
      ? registeredActor
      : null;
  // CRITICAL: only an admitted route registers a validated actor. The outer
  // owner makes no identity read before claim admission, yet keeps the request
  // while claim and permission gates withhold protected children.
  // https://react.dev/learn/preserving-and-resetting-state
  return (
    <ContactActorRegistrationContext.Provider value={registerActor}>
      {contactId && actor ? (
        <ContactNextActionCustody
          key={JSON.stringify([actor.staffId, actor.firebaseUid, contactId])}
          {...actor}
          personId={contactId}
          organisationId={null}
        >
          {children}
        </ContactNextActionCustody>
      ) : (
        children
      )}
    </ContactActorRegistrationContext.Provider>
  );
}
